import { css } from '@emotion/core'
import { ArrowCaret, ContentAttached } from '@fs/zion-icon'
import { Ellipsis, IconButton, PersonBlock, ResponsiveTableRow, Row, colors, H6 } from '@fs/zion-ui'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PlaceTemple } from '@fs/zion-icon/dist/cjs/icons'
import { uniqueId } from 'lodash'
import { DmcResponsiveCheckboxTable } from '../common/DmcResponsiveTable'
import useNotifications from '../../hooks/useNotifications'
import { getTempleInventoryItem } from '../../services/templeInventoryService'
import DmcCopyToClipboardTooltip from '../../util/DmcCopyToClipboardTooltip'
import { getEnumAbbreviation, toTitleCase } from '../../util/StringUtil'
import { getFrontendBaseUrl, useQueryParams } from '../../util/UrlUtil'
import { GroupLink, PersonLink, UnitLink, UserLink } from '../app/Links'
import { PermissionsContext } from '../app/PermissionsContext'
import { PersonContext } from '../person/PersonContext'
import EditUserReservationButton from './EditUserReservationButton'
import { utcToLocaleString } from '../../util/DateUtil'

const hiddenDetailsLabel = css`
  font-size: 0.9rem;
`
const ranIdLabel = css`
  display: flex;
  justify-content: space-between;
}
`
const ranIdLabelText = css`
  align-self: flex-end;
}
`
const ranIdLabelIcon = css`
  position: relative;
  top: 5px;
}
`
const focusedRanIdStyles = css`
  border-radius: 50px;
  animation: glow 1s 6 alternate;

  @keyframes glow {
    from {
      box-shadow: 0 0 5px -5px ${colors.green03};
    }
    to {
      box-shadow: 0 0 5px 20px ${colors.green03};
      background-color: ${colors.green03};
    }
  }
`

const ShowAllHiddenDetailsButton = ({ label, showAllHiddenDetails, updateShowAllHiddenDetailsList }) => {
  return (
    <span css={ranIdLabel}>
      <span css={ranIdLabelText}>{label}</span>
      <span css={ranIdLabelIcon}>
        <IconButton
          Icon={ArrowCaret}
          iconDirection={showAllHiddenDetails ? 'up' : 'down'}
          onClick={() => updateShowAllHiddenDetailsList()}
          data-testid="showAllDetails"
          size="md"
        />
      </span>
    </span>
  )
}

export default function ReservationsWithSharedTypeTable({ reservations, personData, userNames, setCheckedRanIds }) {
  const { t } = useTranslation()
  const { fetchCurrentPerson } = useContext(PersonContext)
  const [showHiddenDetailsList, setShowHiddenDetailList] = useState([])
  const [showAllHiddenDetails, setShowAddHiddenDetails] = useState(false)
  const queryParams = useQueryParams()
  const ranIdToScrollTo = queryParams.get('ranId')
  const { isAuthorized } = useContext(PermissionsContext)
  const { error } = useNotifications()
  const focusedRanIdRef = useRef()

  useEffect(() => {
    async function mapData() {
      const wardAndStakeMap = new Map()
      const sharedReservations = reservations.filter(
        (res) => res.sharedType === 'TEMPLE' && res.osid && res.onList && !res.printedDate
      )
      const osidSet = new Set(sharedReservations.map((res) => res.osid))
      await Promise.allSettled(
        [...osidSet].map(async (osid) => {
          try {
            const inventoryItem = await getTempleInventoryItem(osid)
            wardAndStakeMap.set(osid, { ward: inventoryItem.ward, stake: inventoryItem.stake })
          } catch (err) {
            error(
              t('person.reservations.errorFetchingWardDetails', `Error retrieving ward/stake information for ${osid}`),
              err
            )
          }
        })
      )
      sharedReservations.forEach((res) => {
        res.ward = wardAndStakeMap.get(res.osid)?.ward
        res.stake = wardAndStakeMap.get(res.osid)?.stake
      })
    }
    mapData()
  }, [error, reservations, t])

  useEffect(() => {
    if (ranIdToScrollTo && focusedRanIdRef.current) {
      focusedRanIdRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
      focusedRanIdRef.current.click()
    }
  }, [ranIdToScrollTo])

  const updateShowHiddenDetailsList = (decodedRanId) => {
    const showHiddenDetailsListCopy = JSON.parse(JSON.stringify(showHiddenDetailsList))
    const decodedRanIdIndex = showHiddenDetailsListCopy.findIndex((element) => element === decodedRanId)
    if (decodedRanIdIndex === -1) {
      showHiddenDetailsListCopy.push(decodedRanId)
    } else {
      showHiddenDetailsListCopy.splice(decodedRanIdIndex, 1)
    }
    setShowHiddenDetailList(showHiddenDetailsListCopy)
  }

  const updateShowAllHiddenDetailsList = () => {
    setShowAddHiddenDetails(!showAllHiddenDetails)
  }

  return (
    <DmcResponsiveCheckboxTable
      id="userReservationsTable"
      onSelectionChange={setCheckedRanIds}
      totalColumnCount={10}
      separator={<br />}
      atSizeColumns={{
        default: {
          labels: [
            t('person.label', 'Person'),
            t('reservations.ordinance', 'Ord'),
            t('reservations.ownerSharedTo', 'Owner/Shared'),
            t('reservations.reservedAssigned', 'Reserved/Assigned'),
            t('reservations.printedExpired', 'Printed/Expires'),
            <ShowAllHiddenDetailsButton
              key={uniqueId('showAllHiddenDetails-')}
              label={t('reservations.ranId', 'RAN ID')}
              showAllHiddenDetails={showAllHiddenDetails}
              updateShowAllHiddenDetailsList={updateShowAllHiddenDetailsList}
            />,
          ],
          columnWidths: [3, 1, 2, 2, 2, 2],
          combinationRules: [
            ['column1'],
            ['column2'],
            ['column3', 'column4'],
            ['column5', 'column6'],
            ['column7', 'column8', 'column9'],
            ['column10'],
          ],
        },
        sm: {
          labels: [
            t('person.label', 'Person'),
            t('reservations.ordinanceOwnerShared', 'Ordinance/ Owner/ Shared'),
            t('reservations.reservedAssignedPrintedExpires', 'Reserved/ Shared/ Printed/ Expires'),
            <ShowAllHiddenDetailsButton
              key={uniqueId('showAllHiddenDetails-')}
              label={t('reservations.ranId', 'RAN ID')}
              showAllHiddenDetails={showAllHiddenDetails}
              updateShowAllHiddenDetailsList={updateShowAllHiddenDetailsList}
            />,
          ],
          columnWidths: [3, 3, 4, 2],
          combinationRules: [
            ['column1'],
            ['column2', 'column3', 'column4'],
            ['column5', 'column6', 'column7', 'column8'],
            ['column10', 'column9'],
          ],
        },
        xl: {
          labels: [
            t('person.label', 'Person'),
            t('reservations.ordinance', 'Ord'),
            t('reservations.ownerSharedTo', 'Owner/Shared'),
            t('reservations.reservedAssigned', 'Reserved/Assigned'),
            t('reservations.printedExpired', 'Printed/Expires'),
            <ShowAllHiddenDetailsButton
              key={uniqueId('showAllHiddenDetails-')}
              label={t('reservations.ranId', 'RAN ID')}
              showAllHiddenDetails={showAllHiddenDetails}
              updateShowAllHiddenDetailsList={updateShowAllHiddenDetailsList}
            />,
          ],
          columnWidths: [3, 1, 2, 2, 2, 2],
          combinationRules: [
            ['column1'],
            ['column2'],
            ['column3', 'column4'],
            ['column5', 'column6'],
            ['column7', 'column8', 'column9'],
            ['column10'],
          ],
        },
      }}
    >
      {reservations.map((res) => {
        const shouldFocusRanId = ranIdToScrollTo === res.ranId
        const showHiddenDetails = showAllHiddenDetails || showHiddenDetailsList.includes(res.decodedRanId)
        return (
          <ResponsiveTableRow
            key={res.decodedRanId}
            name={res.decodedRanId}
            column1={
              <PersonReservationPersons
                showHiddenDetails={showHiddenDetails}
                reservation={res}
                personData={personData}
              />
            }
            column2={<Ellipsis>{getEnumAbbreviation(res.ordinanceType)}</Ellipsis>}
            column3={
              <Ellipsis>
                <UserLink showId userId={res.ownerId} userName={userNames && userNames[res.ownerId]} />
              </Ellipsis>
            }
            column4={
              <>
                {res.sharedType === 'GROUP' ? (
                  <Ellipsis>
                    <GroupLink text={`${res.sharedType} (${res.groupId})`} groupId={res.groupId} />
                  </Ellipsis>
                ) : (
                  <Ellipsis>{t(`reservation.sharedType.${res.sharedType}`, res.sharedType)}</Ellipsis>
                )}
                {showHiddenDetails && (
                  <>
                    <br />
                    <strong css={hiddenDetailsLabel}>{`${t('reservations.onUserList', 'On User List')}: `}</strong>
                    {res.onList ? 'Yes' : 'No'}
                    <br />
                  </>
                )}
              </>
            }
            column5={<Ellipsis>{utcToLocaleString(res.reservedDate, true)}</Ellipsis>}
            column6={
              <>
                <Ellipsis>{utcToLocaleString(res.assignedDate, true)}</Ellipsis>
                {showHiddenDetails && (
                  <>
                    <br />
                    <strong css={hiddenDetailsLabel}>{`${t('lastModified', 'Last Modified')}: `}</strong>
                    {res.lastModifiedDate}
                  </>
                )}
              </>
            }
            column7={
              <Ellipsis>
                {res.printedDate
                  ? utcToLocaleString(res.printedDate, true)
                  : t('reservations.notPrinted', 'Not Printed')}
              </Ellipsis>
            }
            column8={
              <>
                <Ellipsis>
                  {res.expiryDate ? (
                    <span
                      style={{
                        color:
                          res.reservationType === 'SHARED_READY' ||
                          res.reservationType === 'FAMILY_GROUP' ||
                          res.reservationType === 'INSTANT_NAME'
                            ? colors.red60
                            : '',
                      }}
                    >
                      {utcToLocaleString(res.expiryDate, true)}
                    </span>
                  ) : (
                    t('reservations.noExpiryDate', 'No Exp Date')
                  )}
                </Ellipsis>
                {showHiddenDetails && res.reservationType != null && (
                  <>
                    <br />
                    <strong css={hiddenDetailsLabel}>{`${t('res.type', 'Type')}: `}</strong>
                    {toTitleCase(res.reservationType)}
                  </>
                )}
              </>
            }
            column9={
              <>
                {showHiddenDetails && res.stake && (
                  <>
                    <strong css={hiddenDetailsLabel}>{`${t('reservations.stake', 'Stake')}: `}</strong>
                    <UnitLink unitnumber={res.stake} />
                    <br />
                  </>
                )}
                {showHiddenDetails && res.ward && (
                  <>
                    <strong css={hiddenDetailsLabel}>{`${t('reservations.ward', 'Ward')}: `}</strong>
                    <UnitLink unitnumber={res.ward} />
                  </>
                )}
              </>
            }
            column10={
              <>
                <Row alignY="middle" wrap gutters="xs">
                  <DmcCopyToClipboardTooltip
                    value={res.ranId}
                    body={shouldFocusRanId && <span css={focusedRanIdStyles}>{res.ranId}</span>}
                  />
                  <EditUserReservationButton reservation={res} onSuccess={fetchCurrentPerson} />
                  <IconButton
                    Icon={ArrowCaret}
                    iconDirection={showHiddenDetails ? 'up' : 'down'}
                    onClick={() => updateShowHiddenDetailsList(res.decodedRanId)}
                    size="xs"
                    ref={shouldFocusRanId ? focusedRanIdRef : undefined}
                  />
                  {res.osid && (
                    <IconButton
                      Icon={ContentAttached}
                      onClick={() =>
                        window.open(
                          `${getFrontendBaseUrl()}/temple/ows/${res.osid}/summary?idType=osid`,
                          '_blank',
                          'noopener, noreferrer'
                        )
                      }
                      description={res.osid}
                      size="xs"
                    />
                  )}
                  {res.pinId && (
                    <IconButton
                      Icon={PlaceTemple}
                      onClick={() =>
                        window.open(
                          `${getFrontendBaseUrl()}/temple/inventory-info/${res.pinId}/summary`,
                          '_blank',
                          'noopener, noreferrer'
                        )
                      }
                      size="xs"
                    />
                  )}
                </Row>
                {res.decodedRanId && (
                  <Row>
                    <span>
                      (
                      <DmcCopyToClipboardTooltip
                        value={res.decodedRanId}
                        body={shouldFocusRanId && <span css={focusedRanIdStyles}>{res.decodedRanId}</span>}
                      />
                      )
                    </span>
                  </Row>
                )}
              </>
            }
            disabled={!isAuthorized('ReserveOrdinancePermission')}
          />
        )
      })}
    </DmcResponsiveCheckboxTable>
  )
}

function ReservationPerson({ showHiddenDetails, gender, name, personId, originalId }) {
  let details = <PersonLink personId={personId} />
  if (showHiddenDetails) {
    details = (
      <>
        Current: <PersonLink personId={personId} />
        <br />
        Original: <PersonLink personId={originalId} />
      </>
    )
  }
  return (
    <div>
      <PersonBlock
        size="sm"
        avatarProps={{
          sex: gender.toLowerCase(),
        }}
        name={name}
        details={details}
      />
    </div>
  )
}

function PersonReservationPersons({ showHiddenDetails, reservation, personData }) {
  const { t } = useTranslation()
  if (reservation.ordinanceType === 'SEALING_TO_PARENTS') {
    const childCurrent = personData[reservation.currentIndividualId]
    const childOriginal = personData[reservation.originalIndividualId]
    const parent1Current = personData[reservation.currentSpouse1Id]
    const parent2Current = personData[reservation.currentSpouse2Id]
    const parent1Original = personData[reservation.originalSpouse1Id]
    const parent2Original = personData[reservation.originalSpouse2Id]
    return (
      <>
        <ReservationPerson
          showHiddenDetails={showHiddenDetails}
          gender={childCurrent.gender}
          name={childCurrent.name}
          personId={childCurrent.personId}
          originalId={childOriginal.personId}
        />
        <H6>{t('parents', 'Parents')}</H6>
        <ReservationPerson
          showHiddenDetails={showHiddenDetails}
          gender={parent1Current.gender}
          name={parent1Current.name}
          personId={parent1Current.personId}
          originalId={parent1Original.personId}
        />
        <ReservationPerson
          showHiddenDetails={showHiddenDetails}
          gender={parent2Current.gender}
          name={parent2Current.name}
          personId={parent2Current.personId}
          originalId={parent2Original.personId}
        />
      </>
    )
  }
  if (reservation.ordinanceType === 'SEALING_TO_SPOUSE') {
    const spouse1Current = personData[reservation.currentSpouse1Id]
    const spouse2Current = personData[reservation.currentSpouse2Id]
    const spouse1Original = personData[reservation.originalSpouse1Id]
    const spouse2Original = personData[reservation.originalSpouse2Id]
    return (
      <>
        <ReservationPerson
          showHiddenDetails={showHiddenDetails}
          gender={spouse1Current.gender}
          name={spouse1Current.name}
          personId={spouse1Current.personId}
          originalId={spouse1Original.personId}
        />
        <H6>{t('spouse', 'Spouse')}</H6>
        <ReservationPerson
          showHiddenDetails={showHiddenDetails}
          gender={spouse2Current.gender}
          name={spouse2Current.name}
          personId={spouse2Original.personId}
          originalId={spouse2Original.personId}
        />
      </>
    )
  }
  const individualCurrent = personData[reservation.currentIndividualId]
  const individualOriginal = personData[reservation.originalIndividualId]
  return (
    <ReservationPerson
      showHiddenDetails={showHiddenDetails}
      gender={individualCurrent.gender}
      name={individualCurrent.name}
      personId={individualCurrent.personId}
      originalId={individualOriginal.personId}
    />
  )
}
